/**
 * Issue FAB Component
 * BRD-DEV-001 | FR-001-005
 *
 * Floating Action Button for quick issue creation in dev mode.
 */

import { useState, useEffect, useCallback } from 'react';
import { BugAntIcon, PhotoIcon, DocumentTextIcon, ArrowDownTrayIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import { cn } from '@myvault/shared/utils/cn';

import { IssueDialog } from './IssueDialog';
import { IssueListDialog } from './IssueListDialog';
import { captureScreenshot, captureConsoleLogs, captureNetworkLogs } from '../utils/screenshotCapture';
import { getConsoleErrors, getNetworkErrors } from '../utils/contextCapture';
import { issueStorage } from '../utils/issueStorage';
import type { IssueAttachment, LocalIssue } from '../types';

interface IssueFABProps {
  /** Position offset from bottom */
  bottomOffset?: number;
  /** Position offset from right */
  rightOffset?: number;
  /** Whether to show pulse animation */
  showPulse?: boolean;
}

export function IssueFAB({
  bottomOffset = 24,
  rightOffset = 24,
  showPulse = true,
}: IssueFABProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isListDialogOpen, setIsListDialogOpen] = useState(false);
  const [isQuickMenuOpen, setIsQuickMenuOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [capturedAttachments, setCapturedAttachments] = useState<IssueAttachment[]>([]);
  const [editingIssue, setEditingIssue] = useState<LocalIssue | null>(null);
  const [longPressTimer, setLongPressTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Only show in development mode
  const isDevMode = import.meta.env.DEV || import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

  // Load issue counts
  useEffect(() => {
    if (!isDevMode) return;

    const loadCounts = async () => {
      const stats = await issueStorage.getStats();
      setPendingCount(stats.pending + stats.draft);
      setTotalCount(stats.total);
    };

    loadCounts();

    // Refresh count periodically
    const interval = setInterval(loadCounts, 30000);
    return () => clearInterval(interval);
  }, [isDevMode]);

  // Handle FAB click
  const handleClick = useCallback(() => {
    if (isQuickMenuOpen) {
      setIsQuickMenuOpen(false);
    } else {
      setIsDialogOpen(true);
    }
  }, [isQuickMenuOpen]);

  // Handle long press start
  const handlePointerDown = useCallback(() => {
    const timer = setTimeout(() => {
      setIsQuickMenuOpen(true);
    }, 500); // 500ms for long press
    setLongPressTimer(timer);
  }, []);

  // Handle long press end
  const handlePointerUp = useCallback(() => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  }, [longPressTimer]);

  // Handle quick screenshot
  const handleQuickScreenshot = async () => {
    setIsQuickMenuOpen(false);
    const screenshot = await captureScreenshot();
    if (screenshot) {
      setCapturedAttachments((prev) => [...prev, screenshot]);
      setIsDialogOpen(true);
    }
  };

  // Handle quick log capture
  const handleQuickLogs = () => {
    setIsQuickMenuOpen(false);
    const consoleErrors = getConsoleErrors();
    const networkErrors = getNetworkErrors();

    const attachments: IssueAttachment[] = [];

    if (consoleErrors.length > 0) {
      attachments.push(captureConsoleLogs(consoleErrors));
    }

    if (networkErrors.length > 0) {
      attachments.push(captureNetworkLogs(networkErrors));
    }

    if (attachments.length > 0) {
      setCapturedAttachments((prev) => [...prev, ...attachments]);
    }

    setIsDialogOpen(true);
  };

  // Handle export for sync
  const handleExportForSync = async () => {
    setIsQuickMenuOpen(false);
    try {
      const result = await issueStorage.downloadForSync();
      if (result.count > 0) {
        // Show a simple alert since we don't have toast access here
        console.info(`Downloaded ${result.count} issues. Save to .myvault-issues/issues.json then run: pnpm issues:sync`);
      } else {
        console.info('No issues to export');
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Handle open list view
  const handleOpenList = () => {
    setIsQuickMenuOpen(false);
    setIsListDialogOpen(true);
  };

  // Handle edit issue from list
  const handleEditIssue = (issue: LocalIssue) => {
    setIsListDialogOpen(false);
    setEditingIssue(issue);
    setIsDialogOpen(true);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setCapturedAttachments([]);
    setEditingIssue(null);
  };

  // Handle list dialog close
  const handleListDialogClose = () => {
    setIsListDialogOpen(false);
  };

  // Handle issue saved
  const handleIssueSaved = async () => {
    const stats = await issueStorage.getStats();
    setPendingCount(stats.pending + stats.draft);
  };

  // Close quick menu when clicking outside
  useEffect(() => {
    if (!isQuickMenuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.issue-fab-container')) {
        setIsQuickMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isQuickMenuOpen]);

  // Keyboard shortcut (Ctrl+Shift+I to open)
  useEffect(() => {
    if (!isDevMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if typing in input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Ctrl+Shift+I to open issue dialog
      if (e.ctrlKey && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        setIsDialogOpen(true);
      }

      // Ctrl+Shift+L to open issue list
      if (e.ctrlKey && e.shiftKey && e.key === 'L') {
        e.preventDefault();
        setIsListDialogOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDevMode]);

  if (!isDevMode) {
    return null;
  }

  return (
    <>
      {/* FAB Container */}
      <div
        className="issue-fab-container fixed z-[9998]"
        style={{
          bottom: bottomOffset,
          right: rightOffset,
        }}
        data-html2canvas-ignore
      >
        {/* Quick Menu (shown on long press) */}
        {isQuickMenuOpen && (
          <div className="absolute bottom-16 right-0 flex flex-col gap-2">
            {/* Issue List Button - Primary action */}
            <button
              onClick={handleOpenList}
              className="flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-lg transition-all hover:bg-blue-700"
              title="View all issues"
            >
              <ListBulletIcon className="h-4 w-4" />
              <span>Issues ({totalCount})</span>
            </button>

            {/* Screenshot Button */}
            <button
              onClick={handleQuickScreenshot}
              className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-lg transition-all hover:bg-gray-50"
              title="Capture Screenshot"
            >
              <PhotoIcon className="h-4 w-4" />
              <span>Screenshot</span>
            </button>

            {/* Log Dump Button */}
            <button
              onClick={handleQuickLogs}
              className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-lg transition-all hover:bg-gray-50"
              title="Capture Logs"
            >
              <DocumentTextIcon className="h-4 w-4" />
              <span>Logs</span>
            </button>

            {/* Export for Sync Button */}
            {pendingCount > 0 && (
              <button
                onClick={handleExportForSync}
                className="flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-medium text-white shadow-lg transition-all hover:bg-amber-600"
                title="Export issues for CLI sync"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span>Export ({pendingCount})</span>
              </button>
            )}
          </div>
        )}

        {/* Main FAB Button */}
        <button
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          className={cn(
            'issue-fab relative flex h-14 w-14 items-center justify-center rounded-full',
            'bg-red-500 text-white shadow-lg',
            'transition-all duration-200 hover:bg-red-600 hover:scale-105',
            'focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2',
            showPulse && 'animate-pulse'
          )}
          title="Report Issue (Ctrl+Shift+I)"
          aria-label="Report Issue"
        >
          <BugAntIcon className="h-6 w-6" />

          {/* Pending Count Badge */}
          {pendingCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1 text-xs font-bold text-white">
              {pendingCount > 9 ? '9+' : pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* Issue Dialog */}
      <IssueDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSaved={handleIssueSaved}
        initialAttachments={capturedAttachments}
        editIssue={editingIssue}
      />

      {/* Issue List Dialog */}
      <IssueListDialog
        open={isListDialogOpen}
        onClose={handleListDialogClose}
        onEditIssue={handleEditIssue}
      />
    </>
  );
}
